import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { validateRut } from '@fdograph/rut-utilities';
import Swal from 'sweetalert2';

import { AppState } from 'src/app/app.reducer';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import {
  PayerInvitation,
  PayerInvitationService,
  PayerLinkView,
} from 'src/app/dashboard/services/payer-invitation.service';
import { PagadorService } from 'src/app/dashboard/services/pagador.service';
import { Pagador } from 'src/app/dashboard/interfaces/pagador';

/** WhatsApp de soporte (el mismo que usa el navbar del dashboard). */
const SUPPORT_WHATSAPP = '56965474761';

/** Link que todavía se puede completar (el pagador aún no termina). */
const ESTADOS_ABIERTOS = ['PENDING', 'OTP_VERIFIED', 'PROCESSING'];

/**
 * Fila del tab "Pendientes": mezcla links sin completar y pagadores que ya
 * completaron y esperan la revisión del operador.
 */
export interface FilaPendiente {
  origen: 'link' | 'pagador';
  nombre: string;
  documento: string;
  tipoUsuarioId: number;
  estado: string;
  fecha: string;
  inv?: PayerInvitation;
}

/**
 * Vista del CLIENTE para generar y gestionar links de invitación de pagadores.
 * El cliente fija país (CL/VE) y tipo (persona/empresa); el pagador completa el
 * KYC + formulario desde el link. La aprobación final del pagador la hace un
 * operador (este flujo solo lo deja "pendiente de revisión").
 */
@Component({
  selector: 'app-payer-invitations',
  templateUrl: './payer-invitations.component.html',
  styleUrls: ['./payer-invitations.component.sass'],
})
export class PayerInvitationsComponent implements OnInit {
  form: FormGroup;
  invitations: PayerInvitation[] = [];
  pagadores: Pagador[] = [];

  /**
   * Un tab por estado, cada uno con su paginador:
   *  - Activos: pagadores habilitados.
   *  - Pendientes: links sin completar + pagadores en revisión del operador.
   *    Un pagador rechazado por compliance sigue acá como "En revisión": al
   *    cliente no se le informa esa decisión (tipping-off).
   *  - Rechazados: links que fallaron la verificación, vencidos o revocados.
   * Los links completados no se listan: ya aparecen como pagador.
   */
  dsActivos = new MatTableDataSource<Pagador>([]);
  dsPendientes = new MatTableDataSource<FilaPendiente>([]);
  dsRechazados = new MatTableDataSource<PayerInvitation>([]);

  // Setters: el contenido de cada tab se crea recién al abrirlo.
  @ViewChild('pagActivos') set pagActivos(p: MatPaginator | undefined) {
    if (p) this.dsActivos.paginator = p;
  }
  @ViewChild('pagPendientes') set pagPendientes(p: MatPaginator | undefined) {
    if (p) this.dsPendientes.paginator = p;
  }
  @ViewChild('pagRechazados') set pagRechazados(p: MatPaginator | undefined) {
    if (p) this.dsRechazados.paginator = p;
  }

  colsActivos = ['nombre', 'documento', 'tipo', 'fecha'];
  colsPendientes = ['nombre', 'documento', 'tipo', 'estado', 'fecha', 'acciones'];
  colsRechazados = ['documento', 'tipo', 'estado', 'contacto', 'fecha', 'acciones'];

  loading = false;
  lastLink: string | null = null;

  /**
   * Link permanente del cliente (12/10/2026): uno solo, sin cargar datos del
   * pagador. Reemplaza al formulario de invitación, que queda oculto.
   */
  miLink: PayerLinkView | null = null;
  /** Panel de reglas abierto/cerrado. */
  verReglas = true;

  /**
   * País del pagador, ATADO al tipo del cliente (no editable):
   *  - cliente VE (`tipoId === 'VE'`)  → pagadores venezolanos (cédula VE).
   *  - cliente Chile (resto)           → pagadores chilenos (documento CL).
   */
  pais: 'CL' | 'VE' = 'CL';

  constructor(
    private fb: FormBuilder,
    private service: PayerInvitationService,
    private spinner: SpinnerService,
    private clipboard: Clipboard,
    private store: Store<AppState>,
    private router: Router,
    private pagadorService: PagadorService,
  ) {
    // El país NO es un control editable: lo define el tipo del cliente.
    // RUT/cédula, email y teléfono son obligatorios.
    this.form = this.fb.group({
      tipoUsuarioId: [0, [Validators.required]],
      rut: ['', [Validators.required]],
      contactEmail: ['', [Validators.required, Validators.email]],
      contactPhone: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    // El país del pagador queda atado al tipo del cliente (tipoId del store auth).
    this.store.select('auth').subscribe(({ user }: any) => {
      this.pais = user?.tipoId === 'VE' ? 'VE' : 'CL';
      this.applyMaxLengths();
      // El backend toma al cliente del token; el id del path solo completa la ruta.
      // En un refresh el store llega vacío y se completa después: ahí se recarga.
      const usuarioId = user?.usuarioId;
      if (usuarioId && usuarioId !== this.usuarioId) {
        this.usuarioId = usuarioId;
        if (this.iniciado) this.load();
      }
    });
    this.iniciado = true;
    this.load();
  }

  private iniciado = false;

  private usuarioId?: number;

  /**
   * Extrae el mensaje de error a mostrar. El ErrorInterceptor global aplana los
   * errores que NO son 400/422/429 (p. ej. el 409 Conflict de la validación de
   * documento: "ya es cliente" / "ya es pagador") a un string, así que hay que
   * contemplar ambos casos para no perder el motivo real.
   */
  private errorText(e: any, fallback = 'Intenta nuevamente.'): string {
    // El rate limit (10/min por usuario) responde en inglés ("ThrottlerException:
    // Too Many Requests"); se traduce.
    if (e?.status === 429) {
      return 'Hiciste muchas acciones seguidas. Espera un minuto y vuelve a intentarlo.';
    }
    if (typeof e === 'string' && e.trim()) return e;
    return e?.error?.message || fallback;
  }

  /** Ajusta los validadores de longitud máxima según el país resuelto. */
  private applyMaxLengths(): void {
    this.form.get('rut')?.setValidators([
      Validators.required,
      Validators.maxLength(this.docMaxLength),
    ]);
    this.form.get('contactPhone')?.setValidators([
      Validators.required,
      Validators.maxLength(this.phoneMaxLength),
    ]);
    this.form.get('contactEmail')?.setValidators([
      Validators.required,
      Validators.email,
      Validators.maxLength(this.emailMaxLength),
    ]);
    this.form.get('rut')?.updateValueAndValidity();
    this.form.get('contactPhone')?.updateValueAndValidity();
    this.form.get('contactEmail')?.updateValueAndValidity();
  }

  get paisLabel(): string {
    return this.pais === 'VE' ? 'Venezuela' : 'Chile';
  }

  /** Tope de caracteres del documento según país (CL RUT vs VE cédula). */
  get docMaxLength(): number {
    return this.pais === 'VE' ? 10 : 12;
  }
  /** Tope de caracteres del teléfono E.164 según país. */
  get phoneMaxLength(): number {
    return this.pais === 'VE' ? 13 : 12;
  }
  /** Tope de email (máx RFC 5321), igual para ambos países. */
  readonly emailMaxLength = 254;

  get docPlaceholder(): string {
    return this.pais === 'VE' ? 'V-12345678' : '12.345.678-9';
  }
  get phonePlaceholder(): string {
    return this.pais === 'VE' ? '+584121234567' : '+56912345678';
  }

  /** El cliente ve el pagador creado en su listado "Mis pagadores". */
  verPagador(): void {
    this.router.navigate(['/wallet/client/payerList']);
  }

  async load(): Promise<void> {
    this.loading = true;
    // Cada parte carga por separado: si una falla (p. ej. la base no da
    // conexiones y el listado de pagadores responde 502), las otras se
    // muestran igual y se conserva lo último que se tenía de la que falló.
    const [inv, pag, link] = await Promise.allSettled([
      this.service.list(),
      this.usuarioId
        ? this.pagadorService.getPagadoresByUser(this.usuarioId)
        : Promise.resolve(this.pagadores),
      this.service.getLink(),
    ]);
    const fallas: string[] = [];
    if (inv.status === 'fulfilled') this.invitations = inv.value;
    else fallas.push(`registros (${this.errorText(inv.reason, 'sin respuesta')})`);
    if (pag.status === 'fulfilled') this.pagadores = pag.value ?? [];
    else fallas.push(`pagadores (${this.errorText(pag.reason, 'sin respuesta')})`);
    // Un backend sin el link permanente no es un error que mostrar: queda sin tarjeta.
    if (link.status === 'fulfilled') this.miLink = link.value;
    this.distribuir();
    this.loading = false;
    if (fallas.length) {
      Swal.fire({
        icon: 'warning',
        title: 'No se pudo actualizar todo',
        text: `No cargaron: ${fallas.join(', ')}. Se muestra lo último disponible; intenta de nuevo en unos minutos.`,
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  async crear(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { rut, contactEmail, contactPhone } = this.form.value;
    if (!contactEmail || !contactPhone) {
      Swal.fire({
        icon: 'warning',
        title: 'Faltan contactos',
        text: 'Ingresa el email y el teléfono del pagador.',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }
    // Validación del documento antes de llamar al backend, según el país.
    if (this.pais === 'CL' && !validateRut((rut || '').toString())) {
      Swal.fire({
        icon: 'warning',
        title: 'RUT inválido',
        text: 'Revisa el RUT del pagador (ej. 12.345.678-9).',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }
    if (this.pais === 'VE' && !this.esCedulaVeValida((rut || '').toString())) {
      Swal.fire({
        icon: 'warning',
        title: 'Documento inválido',
        text: 'Revisa la cédula del pagador (ej. V-12345678).',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }
    // Teléfono en formato internacional (código de país + número).
    if (!/^\+\d{8,15}$/.test((contactPhone || '').toString().trim())) {
      Swal.fire({
        icon: 'warning',
        title: 'Teléfono inválido',
        text: `Ingresa el teléfono con código de país (ej. ${this.phonePlaceholder}).`,
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }
    const ref = this.spinner.start();
    try {
      const res = await this.service.create({
        pais: this.pais,
        tipoUsuarioId: Number(this.form.value.tipoUsuarioId),
        rut: (rut || '').toString().trim(),
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
      });
      this.spinner.stop(ref);
      this.lastLink = res.url;
      this.mostrarLink(res.url);
      this.form.patchValue({ rut: '', contactEmail: '', contactPhone: '' });
      await this.load();
    } catch (e: any) {
      this.spinner.stop(ref);
      this.mostrarErrorRegistro();
    }
  }

  async revocar(inv: PayerInvitation): Promise<void> {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Revocar el link?',
      text: `El link ${inv.shortCode} dejará de funcionar.`,
      showCancelButton: true,
      confirmButtonText: 'Revocar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c0392b',
    });
    if (!confirm.isConfirmed) return;
    const ref = this.spinner.start();
    try {
      await this.service.revoke(inv.id);
      this.spinner.stop(ref);
      await this.load();
    } catch (e: any) {
      this.spinner.stop(ref);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo revocar',
        text: this.errorText(e),
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  async reenviar(inv: PayerInvitation): Promise<void> {
    const ref = this.spinner.start();
    try {
      const res = await this.service.resend(inv.id);
      this.spinner.stop(ref);
      this.lastLink = res.url;
      this.mostrarLink(res.url);
      await this.load();
    } catch (e: any) {
      this.spinner.stop(ref);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo reenviar',
        text: this.errorText(e),
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  // ---------- Link permanente ----------

  get motivoLink(): string {
    switch (this.miLink?.motivo) {
      case 'CUENTA_NO_HABILITADA':
        return 'Tu enlace está pausado porque tu cuenta no está habilitada para recibir pagadores. Si crees que es un error, escríbenos.';
      case 'PAUSADO_POR_REVISION':
        return `Tienes ${this.miLink.pendientes} pagadores en revisión. El enlace se reactiva solo cuando terminemos de revisarlos.`;
      default:
        return '';
    }
  }

  compartirWhatsapp(url: string): void {
    const texto = `Para poder pagarme por Aguacate, regístrate como mi pagador desde este enlace. Necesitas tu cédula y tu celular a mano:\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
  }

  async regenerarLink(): Promise<void> {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Regenerar tu enlace?',
      text: 'El enlace actual deja de funcionar al instante, también para quien esté registrándose con él. Tendrás que compartir el nuevo.',
      showCancelButton: true,
      confirmButtonText: 'Regenerar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c0392b',
    });
    if (!confirm.isConfirmed) return;
    const ref = this.spinner.start();
    try {
      this.miLink = await this.service.rotateLink();
      this.spinner.stop(ref);
      Swal.fire({ icon: 'success', title: 'Enlace regenerado', timer: 1400, showConfirmButton: false });
    } catch (e: any) {
      this.spinner.stop(ref);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo regenerar',
        text: this.errorText(e),
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  copiar(url: string): void {
    this.clipboard.copy(url);
    Swal.fire({
      icon: 'success',
      title: 'Link copiado',
      timer: 1200,
      showConfirmButton: false,
    });
  }

  /** Formato básico de cédula/RIF venezolano: V/E/J/G opcional + 6 a 9 dígitos. */
  private esCedulaVeValida(doc: string): boolean {
    return /^[VvEeJjGg]?-?\d{6,9}$/.test((doc || '').trim());
  }

  /**
   * Modal del link recién generado.
   *
   * El link SOLO se puede obtener acá: por seguridad el backend no lo vuelve a
   * exponer. Por eso el aviso es explícito ("cópialo ahora, no vuelve a
   * mostrarse") y el modal queda abierto para copiar y además compartir por
   * WhatsApp sin perderlo.
   */
  private mostrarLink(url: string): void {
    const waUrl = `https://wa.me/?text=${encodeURIComponent(
      `Te invito a registrarte como pagador en Aguacate. Completa tus datos desde este link:\n${url}`,
    )}`;
    Swal.fire({
      icon: 'success',
      title: 'Link de pagador generado',
      html: `
        <div style="text-align:left;color:#374151">
          <div style="display:flex;gap:9px;align-items:flex-start;background:#fef3c7;border:1px solid #fcd34d;border-radius:10px;padding:11px 13px;margin-bottom:14px;font-size:.84rem;line-height:1.45">
            <span style="font-size:1.05rem;line-height:1.2">⚠️</span>
            <span><b>Cópialo ahora.</b> Por seguridad este link <b>solo se muestra en esta pantalla</b> y no vuelve a aparecer. Si lo pierdes, tendrás que generar uno nuevo.</span>
          </div>
          <p style="margin:0 0 6px;color:#6b7280;font-size:.8rem">Compártelo con el pagador para que complete su registro:</p>
          <textarea id="pi-link" readonly rows="3" onclick="this.select()" style="width:100%;font-size:12px;border:1px solid #d1d5db;border-radius:8px;padding:9px;font-family:monospace;color:#1e5a3f;background:#f9fafb;resize:none;box-sizing:border-box">${url}</textarea>
          <div style="display:flex;gap:8px;margin-top:12px">
            <button type="button" id="pi-copy" style="flex:1;background:#1e5a3f;color:#fff;border:none;border-radius:9px;padding:11px;font-weight:600;font-size:.86rem;cursor:pointer">Copiar link</button>
            <button type="button" id="pi-wa" style="flex:1;background:#25d366;color:#fff;border:none;border-radius:9px;padding:11px;font-weight:600;font-size:.86rem;cursor:pointer">Compartir por WhatsApp</button>
          </div>
        </div>`,
      showConfirmButton: true,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#6b7280',
      didOpen: () => {
        document.getElementById('pi-copy')?.addEventListener('click', () => {
          this.clipboard.copy(url);
          const b = document.getElementById('pi-copy') as HTMLButtonElement | null;
          if (b) {
            b.textContent = '✓ Copiado';
            b.style.background = '#167c3c';
          }
        });
        document.getElementById('pi-wa')?.addEventListener('click', () => {
          window.open(waUrl, '_blank');
        });
      },
    });
  }

  /**
   * Vista de error cuando no se pudo registrar al pagador.
   *
   * A propósito NO expone el motivo del backend (p. ej. "ya es cliente" / "ya
   * es pagador"): no se le revela al cliente el estado de registro de un tercero.
   * Mensaje genérico + contacto directo por WhatsApp de soporte.
   */
  private mostrarErrorRegistro(): void {
    const waUrl = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
      'Hola, tuve un problema al registrar un pagador en Aguacate y necesito ayuda.',
    )}`;
    Swal.fire({
      icon: 'error',
      title: 'No pudimos registrar el pagador',
      html: `
        <div style="text-align:left;color:#374151">
          <p style="margin:0 0 12px;font-size:.9rem">En este momento no se puede registrar al pagador. Vuelve a intentarlo en unos minutos.</p>
          <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:11px 13px;font-size:.84rem;line-height:1.45;color:#166534">
            ¿Necesitas ayuda? Escríbenos y te acompañamos a registrar al pagador.
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Contactar por WhatsApp',
      cancelButtonText: 'Cerrar',
      confirmButtonColor: '#25d366',
    }).then((r) => {
      if (r.isConfirmed) window.open(waUrl, '_blank');
    });
  }

  /** Reparte links y pagadores en los tres tabs. */
  private distribuir(): void {
    const ahora = Date.now();
    const abierto = (i: PayerInvitation) =>
      ESTADOS_ABIERTOS.includes(i.status) &&
      (i.status === 'PROCESSING' || new Date(i.expiresAt).getTime() > ahora);

    const habilitado = (p: any) => p?.habilitado ?? false;
    this.dsActivos.data = this.pagadores.filter(habilitado);

    const linksAbiertos: FilaPendiente[] = this.invitations.filter(abierto).map((i) => ({
      origen: 'link',
      nombre: i.linkPermanente ? 'Registro en curso' : 'Link enviado',
      documento: this.texto(i.numeroId),
      tipoUsuarioId: i.tipoUsuarioId,
      estado: this.estadoLabel(i.status, i),
      fecha: i.createdAt,
      inv: i,
    }));
    const enRevision: FilaPendiente[] = this.pagadores
      .filter((p) => !habilitado(p))
      .map((p: any) => ({
        origen: 'pagador',
        nombre: this.nombrePagador(p),
        documento: this.documentoPagador(p),
        tipoUsuarioId: Number(p?.tipoUsuarioId) === 1 ? 1 : 0,
        estado: 'En revisión',
        fecha: p?.created_at,
      }));
    this.dsPendientes.data = [...linksAbiertos, ...enRevision].sort(
      (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
    );

    this.dsRechazados.data = this.invitations.filter(
      (i) => i.status !== 'COMPLETED' && !abierto(i),
    );
  }

  /** Estado del tab Rechazados: un link abierto que venció se muestra Expirado. */
  estadoRechazado(i: PayerInvitation): string {
    return ESTADOS_ABIERTOS.includes(i.status) ? 'Expirado' : this.estadoLabel(i.status);
  }

  /** Empresa: nombre comercial o razón social. Persona: nombre y apellidos. */
  nombrePagador(p: any): string {
    if (Number(p?.tipoUsuarioId) === 1) return this.texto(p.nombreComercial || p.razonSocial);
    return this.texto([p?.nombre, p?.apellidos].filter(Boolean).join(' '));
  }

  /** Empresa: RUT de la empresa. Persona: su documento. */
  documentoPagador(p: any): string {
    return this.texto(Number(p?.tipoUsuarioId) === 1 ? p.rutEmpresa : p?.numeroId);
  }

  /** Un campo vacío se muestra "—". */
  texto(valor: unknown): string {
    const s = valor == null ? '' : String(valor).trim();
    return s || '—';
  }

  tipoLabel(t: number): string {
    return t === 1 ? 'Empresa' : 'Persona';
  }

  estadoLabel(s: string, inv?: PayerInvitation): string {
    if (inv?.linkPermanente && s === 'PENDING') return 'Verificando datos';
    const map: Record<string, string> = {
      PENDING: 'Link enviado',
      OTP_VERIFIED: 'Teléfono verificado',
      PROCESSING: 'Verificando',
      COMPLETED: 'Completado',
      REJECTED: 'Rechazado',
      EXPIRED: 'Expirado',
      REVOKED: 'Revocado',
    };
    return map[s] ?? s;
  }

  puedeGestionar(inv: PayerInvitation): boolean {
    return inv.status !== 'COMPLETED';
  }
}
